import { useIsWideViewport } from '@hue/design-system';
import { useState, type ReactNode } from 'react';
import { useCollectionView } from './use-collection-view.js';
import { SortableColumn } from './data-view.js';

export interface RecordColumn<T> {
  key: string;
  label: string;
  render: (record: T) => ReactNode;
  value?: (record: T) => string | number;
  width?: string;
}

/** Uma apresentação dos mesmos registros e ações nas duas larguras, sem duplicar consultas. */
export function RecordCollection<T>({
  label,
  records,
  columns,
  recordKey,
  actions,
  actionsWidth = '9rem',
  details,
  card,
  onOpen,
  sorting,
}: {
  label: string;
  records: readonly T[];
  columns: readonly RecordColumn<T>[];
  recordKey: (record: T) => string;
  actions?: (record: T) => ReactNode;
  /** Cabe três ícones; quem tem mais ações informa a largura para não quebrar a linha. */
  actionsWidth?: string;
  details?: (record: T) => ReactNode;
  card: (record: T) => ReactNode;
  onOpen?: (record: T) => void;
  sorting?: {
    sort: string;
    direction: 'asc' | 'desc';
    columns: readonly string[];
    onToggle: (column: string) => void;
  };
}) {
  const view = useCollectionView();
  const wide = useIsWideViewport();
  const [sort, setSort] = useState('');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const activeSort = sorting?.sort ?? sort;
  const activeDirection = sorting?.direction ?? direction;
  const column = columns.find((item) => item.key === sort);
  const shown =
    !sorting && column?.value
      ? [...records].sort((a, b) => {
          const left = column.value!(a);
          const right = column.value!(b);
          const result =
            typeof left === 'number' && typeof right === 'number'
              ? left - right
              : String(left).localeCompare(String(right), 'pt-BR', { numeric: true });
          return direction === 'asc' ? result : -result;
        })
      : records;
  return (
    <div className="hue-record-collection" data-view={view}>
      {view === 'cards' || !wide ? (
        <div className="grid gap-3 md:grid-cols-2">
          {shown.map((record) => (
            <div key={recordKey(record)}>{card(record)}</div>
          ))}
        </div>
      ) : null}
      {view === 'list' && wide ? (
        <div className="hidden overflow-hidden rounded-2xl border border-surface-border bg-surface-card md:block">
          <table className="w-full table-fixed border-collapse text-left text-sm">
            <caption className="sr-only">
              {label}
              {sorting ? '' : '. Ordenação dos registros desta página.'}
            </caption>
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={column.width ? { width: column.width } : undefined} />
              ))}
              {actions ? <col style={{ width: actionsWidth }} /> : null}
            </colgroup>
            <thead>
              <tr className="border-b border-surface-border">
                {columns.map((item) =>
                  (sorting ? sorting.columns.includes(item.key) : item.value) ? (
                    <SortableColumn
                      key={item.key}
                      column={item.key}
                      label={item.label}
                      sort={activeSort}
                      direction={activeDirection}
                      onToggle={(key) => {
                        if (sorting) {
                          sorting.onToggle(key);
                          return;
                        }
                        setDirection(key === sort && direction === 'asc' ? 'desc' : 'asc');
                        setSort(key);
                      }}
                    />
                  ) : (
                    <th key={item.key} scope="col" className="px-4 py-3 text-xs text-content-muted">
                      {item.label}
                    </th>
                  ),
                )}
                {actions ? (
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {shown.map((record) => (
                <RecordRow
                  key={recordKey(record)}
                  record={record}
                  columns={columns}
                  actions={actions}
                  details={details}
                  onOpen={onOpen}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
function RecordRow<T>({
  record,
  columns,
  actions,
  details,
  onOpen,
}: {
  record: T;
  columns: readonly RecordColumn<T>[];
  actions: ((record: T) => ReactNode) | undefined;
  details: ((record: T) => ReactNode) | undefined;
  onOpen: ((record: T) => void) | undefined;
}) {
  const extra = details?.(record);
  return (
    <>
      <tr
        className={
          onOpen ? 'cursor-pointer transition hover:bg-content/5' : 'transition hover:bg-content/5'
        }
        onClick={(event) => {
          if (
            onOpen &&
            !(event.target instanceof Element && event.target.closest('button, a, input'))
          )
            onOpen(record);
        }}
      >
        {columns.map((column) => (
          <td key={column.key} className="break-words px-4 py-3 align-middle">
            {column.render(record)}
          </td>
        ))}
        {actions ? (
          <td className="px-3 py-2 align-middle">
            <div className="flex justify-end gap-1">{actions(record)}</div>
          </td>
        ) : null}
      </tr>
      {extra ? (
        <tr>
          <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 pb-3">
            {extra}
          </td>
        </tr>
      ) : null}
    </>
  );
}
